import type { MeasureDateRange } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { FieldConfigurationType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type {
  MeasureDescriptor,
  StatementLineConfiguration,
  StatementLineType,
} from "../../../../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../../../../api/types/svTypes.ts";

const MIN_LEVEL = 0;
export const MAX_LEVEL = 4;

export const isValueLine = (line: Pick<StatementLineConfiguration, "lineType">): boolean =>
  line.lineType !== "Group" && line.lineType !== "Spacer";

export const isStatementLineMeasure = (
  measure: Pick<MeasureDescriptor, "type" | "capabilities" | "configuration">
): boolean =>
  measure.type === ItemDataType.Numeric &&
  !measure.capabilities.isScalar &&
  (measure.configuration?.type ?? FieldConfigurationType.Measure) === FieldConfigurationType.Measure;

const hasLaterChildOfLevel = (
  lines: Pick<StatementLineConfiguration, "level">[],
  index: number,
  ancestorLevel: number
): boolean => {
  for (let i = index + 1; i < lines.length; i++) {
    const level = defined(lines[i]).level;
    if (level <= ancestorLevel) {
      return false;
    }
    if (level === ancestorLevel + 1) {
      return true;
    }
  }
  return false;
};

export interface StatementLineRail {
  level: number;
  continues: boolean;
}

export const getLineRails = (lines: Pick<StatementLineConfiguration, "level">[]): StatementLineRail[][] =>
  lines.map((line, index) =>
    Array.from({ length: line.level }, (_, level) => ({ level, continues: hasLaterChildOfLevel(lines, index, level) }))
  );

export const indentLine = <T extends Pick<StatementLineConfiguration, "guid" | "level">>(
  lines: T[],
  guid: string
): T[] => lines.map((line) => (line.guid === guid ? { ...line, level: Math.min(MAX_LEVEL, line.level + 1) } : line));

export const outdentLine = <T extends Pick<StatementLineConfiguration, "guid" | "level">>(
  lines: T[],
  guid: string
): T[] => lines.map((line) => (line.guid === guid ? { ...line, level: Math.max(MIN_LEVEL, line.level - 1) } : line));

export const moveLine = <T extends Pick<StatementLineConfiguration, "guid">>(
  lines: T[],
  guid: string,
  direction: "up" | "down"
): T[] => {
  const index = lines.findIndex((line) => line.guid === guid);
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  if (index === -1 || targetIndex < 0 || targetIndex >= lines.length) {
    return lines;
  }

  const result = [...lines];
  const current = defined(result[index]);
  result[index] = defined(result[targetIndex]);
  result[targetIndex] = current;
  return result;
};

export type InsertLineAnchor = { afterGuid: string } | { beforeGuid: string } | "end";

export type LineDropTarget = { beforeGuid: string } | "end";

const getBlockDepth = <T extends Pick<StatementLineConfiguration, "level">>(block: T[]): number =>
  Math.max(MIN_LEVEL, ...block.map((line) => line.level));

const placeBlockAtLevel = <T extends Pick<StatementLineConfiguration, "level">>(
  block: T[],
  anchorLevel: number
): T[] => {
  const baseLevel = Math.max(MIN_LEVEL, Math.min(anchorLevel, MAX_LEVEL - getBlockDepth(block)));
  return block.map((line) => ({ ...line, level: baseLevel + line.level }));
};

const findChildrenEnd = <T extends Pick<StatementLineConfiguration, "level">>(lines: T[], anchorIndex: number): number => {
  const anchorLevel = defined(lines[anchorIndex]).level;
  let end = anchorIndex + 1;
  while (end < lines.length && defined(lines[end]).level > anchorLevel) {
    end += 1;
  }
  return end;
};

const getPositionAfter = <T extends Pick<StatementLineConfiguration, "level" | "lineType">>(
  lines: T[],
  block: T[],
  anchorIndex: number
): { index: number; level: number } => {
  const anchorLevel = defined(lines[anchorIndex]).level;
  const childrenEnd = findChildrenEnd(lines, anchorIndex);
  if (childrenEnd === anchorIndex + 1) {
    return { index: anchorIndex + 1, level: anchorLevel };
  }
  if (anchorLevel + 1 + getBlockDepth(block) <= MAX_LEVEL) {
    return { index: anchorIndex + 1, level: anchorLevel + 1 };
  }
  const closingLine = lines[childrenEnd];
  const closesAnchor = closingLine?.lineType === "Subtotal" && closingLine.level === anchorLevel;
  return { index: closesAnchor ? childrenEnd + 1 : childrenEnd, level: anchorLevel };
};

export const insertLines = <T extends Pick<StatementLineConfiguration, "guid" | "level" | "lineType">>(
  lines: T[],
  block: T[],
  anchor: InsertLineAnchor
): T[] => {
  if (anchor === "end") {
    return [...lines, ...placeBlockAtLevel(block, lines.at(-1)?.level ?? MIN_LEVEL)];
  }

  const anchorGuid = "afterGuid" in anchor ? anchor.afterGuid : anchor.beforeGuid;
  const anchorIndex = lines.findIndex((candidate) => candidate.guid === anchorGuid);
  const { index, level } =
    "afterGuid" in anchor
      ? getPositionAfter(lines, block, anchorIndex)
      : { index: anchorIndex, level: defined(lines[anchorIndex]).level };

  return [...lines.slice(0, index), ...placeBlockAtLevel(block, level), ...lines.slice(index)];
};

export const insertLine = <T extends Pick<StatementLineConfiguration, "guid" | "level" | "lineType">>(
  lines: T[],
  line: T,
  anchor: InsertLineAnchor
): T[] => insertLines(lines, [{ ...line, level: MIN_LEVEL }], anchor);

export const moveLineTo = <T extends Pick<StatementLineConfiguration, "guid">>(
  lines: T[],
  guid: string,
  target: LineDropTarget
): T[] => {
  const line = lines.find((candidate) => candidate.guid === guid);
  if (!line || (target !== "end" && target.beforeGuid === guid)) {
    return lines;
  }

  const others = lines.filter((candidate) => candidate.guid !== guid);
  const targetIndex =
    target === "end" ? others.length : others.findIndex((candidate) => candidate.guid === target.beforeGuid);
  if (targetIndex === -1) {
    return lines;
  }

  return [...others.slice(0, targetIndex), line, ...others.slice(targetIndex)];
};

export const removeLine = <T extends Pick<StatementLineConfiguration, "guid" | "lineType" | "lineIds">>(
  lines: T[],
  guid: string
): T[] =>
  lines
    .filter((line) => line.guid !== guid)
    .map((line) => (line.lineType === "Total" ? { ...line, lineIds: line.lineIds.filter((id) => id !== guid) } : line));

const findContentLine = <T extends Pick<StatementLineConfiguration, "lineType">>(
  lines: T[],
  from: number,
  step: 1 | -1
): T | undefined => {
  for (let i = from; i >= 0 && i < lines.length; i += step) {
    const line = defined(lines[i]);
    if (line.lineType !== "Spacer") {
      return line;
    }
  }
  return undefined;
};

const getSpacerLevel = <T extends Pick<StatementLineConfiguration, "lineType" | "level">>(
  lines: T[],
  index: number
): number => {
  const next = findContentLine(lines, index + 1, 1);
  if (!next) {
    return MIN_LEVEL;
  }

  const closesGroup = next.lineType === "Subtotal" || next.lineType === "Total";
  const previous = findContentLine(lines, index - 1, -1);
  return closesGroup && previous !== undefined && previous.level > next.level ? next.level + 1 : next.level;
};

export const normalizeSpacerLevels = <T extends Pick<StatementLineConfiguration, "lineType" | "level">>(
  lines: T[]
): T[] => {
  const normalized = lines.map((line, index) => {
    if (line.lineType !== "Spacer") {
      return line;
    }
    const level = getSpacerLevel(lines, index);
    return level === line.level ? line : { ...line, level };
  });
  return normalized.some((line, index) => line !== lines[index]) ? normalized : lines;
};

export const readsPeriodFromField = (lineType: StatementLineType): boolean =>
  lineType === "Amount" || lineType === "Dimension";

export const hasOwnPeriod = (lineType: StatementLineType): boolean =>
  lineType === "Group" || readsPeriodFromField(lineType);

const ownPeriod = (line: Pick<StatementLineConfiguration, "lineType" | "dateRange">): MeasureDateRange | undefined =>
  hasOwnPeriod(line.lineType) ? line.dateRange : undefined;

type PeriodLine = Pick<StatementLineConfiguration, "guid" | "lineType" | "level" | "dateRange">;

const findPeriodAncestorAt = <T extends PeriodLine>(lines: T[], lineIndex: number): T | undefined => {
  let level = defined(lines[lineIndex]).level;

  for (let i = lineIndex - 1; i >= 0; i--) {
    const candidate = defined(lines[i]);
    if (candidate.level >= level) {
      continue;
    }

    if (ownPeriod(candidate) !== undefined) {
      return candidate;
    }

    level = candidate.level;
  }

  return undefined;
};

export const findPeriodAncestor = <T extends PeriodLine>(lines: T[], guid: string): T | undefined => {
  const index = lines.findIndex((line) => line.guid === guid);
  return index === -1 ? undefined : findPeriodAncestorAt(lines, index);
};

export const resolveLinePeriods = (
  lines: PeriodLine[],
  defaultRange: MeasureDateRange
): Record<string, MeasureDateRange> => {
  const result: Record<string, MeasureDateRange> = {};

  lines.forEach((line, index) => {
    if (!readsPeriodFromField(line.lineType)) {
      return;
    }

    result[line.guid] = ownPeriod(line) ?? findPeriodAncestorAt(lines, index)?.dateRange ?? defaultRange;
  });

  return result;
};

export const getSubtotalMembers = (
  lines: Pick<StatementLineConfiguration, "guid" | "lineType" | "level">[],
  guid: string
): string[] => {
  const subtotalIndex = lines.findIndex((line) => line.guid === guid);
  const subtotal = defined(lines[subtotalIndex]);
  const members: string[] = [];

  for (let i = subtotalIndex - 1; i >= 0 && defined(lines[i]).level > subtotal.level; i--) {
    const line = defined(lines[i]);
    if (line.lineType === "Amount" || line.lineType === "Dimension") {
      members.push(line.guid);
    }
  }

  return members.reverse();
};
